import { emptyFurnaceAndThresholds } from "components/common/plan/emptyFurnaceAndThresholds";
import { minimumPeriod } from "hooks/periodIndex";
import {
  FixedMixCreate,
  ProductionMixResultRead,
  SteelGrade,
} from "store/api/generatedApi";
import { Row } from "components/common/plan/types";
import {
  makeRows,
  tableRowsToBackendMixes,
} from "components/common/plan/utils";

import { fixedMixMatches, fixedMixesMatch } from "./fixedMixEdits";

export interface FixedMixGroup {
  mixId: number;
  gradeNames: string[];
}

export const groupFixedMixes = ({
  mixGroups,
  mixes,
}: {
  mixGroups: string[][];
  mixes: FixedMixCreate[];
}): FixedMixGroup[] =>
  mixGroups.flatMap((gradeNames) =>
    mixes.reduce<FixedMixGroup[]>((groups, recipe, index) => {
      if (!gradeNames.includes(recipe.steel_grade_name)) return groups;
      const shared = groups.find((group) => {
        const candidate = mixes.find(
          (candidate) => candidate.steel_grade_name === group.gradeNames[0]
        );
        return candidate && fixedMixMatches(recipe.mix, candidate.mix);
      });
      return shared
        ? groups.map((group) =>
            group === shared
              ? {
                  ...group,
                  gradeNames: [...group.gradeNames, recipe.steel_grade_name],
                }
              : group
          )
        : [
            ...groups,
            { mixId: index + 1, gradeNames: [recipe.steel_grade_name] },
          ];
    }, [])
  );

export const splitFixedMixGroups = ({
  mixGroups,
  gradeNames,
}: {
  mixGroups: string[][];
  gradeNames: string[];
}): string[][] =>
  mixGroups.flatMap((group) =>
    [
      group.filter((name) => !gradeNames.includes(name)),
      group.filter((name) => gradeNames.includes(name)),
    ].filter((group) => group.length > 0)
  );

export const fixedMixGroupsMatch = (
  left: string[][],
  right: string[][]
): boolean => {
  const canonical = (groups: string[][]) =>
    groups.map((group) => JSON.stringify([...group].sort())).sort();
  return JSON.stringify(canonical(left)) === JSON.stringify(canonical(right));
};

export const fixedMixDraftsMatch = (
  first: { mixes: FixedMixCreate[]; mixGroups: string[][] },
  second: { mixes: FixedMixCreate[]; mixGroups: string[][] }
): boolean =>
  fixedMixesMatch(first.mixes, second.mixes) &&
  fixedMixGroupsMatch(first.mixGroups, second.mixGroups);

export const revertFixedMixGrades = ({
  mixes,
  mixGroups,
  defaults,
  gradeNames,
}: {
  mixes: FixedMixCreate[];
  mixGroups: string[][];
  defaults: FixedMixCreate[];
  gradeNames: string[];
}) => {
  const restoredMixes = mixes.flatMap((recipe) =>
    gradeNames.includes(recipe.steel_grade_name)
      ? (defaults.find(
          (candidate) => candidate.steel_grade_name === recipe.steel_grade_name
        ) ?? [])
      : recipe
  );
  return {
    mixes: restoredMixes,
    mixGroups: groupFixedMixes({ mixes: restoredMixes, mixGroups }).map(
      (group) => group.gradeNames
    ),
  };
};

export const restoreFixedMixGroup = ({
  mixes,
  mixGroups,
  sourceMixes,
  sourceGroups,
  gradeNames,
}: {
  mixes: FixedMixCreate[];
  mixGroups: string[][];
  sourceMixes: FixedMixCreate[];
  sourceGroups: string[][];
  gradeNames: string[];
}) => {
  const restored = sourceGroups
    .filter((group) => group.some((name) => gradeNames.includes(name)))
    .map((group) =>
      group.filter((name) => {
        if (gradeNames.includes(name)) return true;
        const recipe = mixes.find((recipe) => recipe.steel_grade_name === name);
        const source = sourceMixes.find(
          (recipe) => recipe.steel_grade_name === name
        );
        return recipe && source && fixedMixMatches(recipe.mix, source.mix);
      })
    );
  const restoredNames = new Set([...gradeNames, ...restored.flat()]);
  return {
    mixes: mixes.flatMap((recipe) =>
      gradeNames.includes(recipe.steel_grade_name)
        ? (sourceMixes.find(
            (source) => source.steel_grade_name === recipe.steel_grade_name
          ) ?? [])
        : recipe
    ),
    mixGroups: [
      ...mixGroups
        .map((group) => group.filter((name) => !restoredNames.has(name)))
        .filter((group) => group.length > 0),
      ...restored,
    ],
  };
};

export const fixedMixResult = ({
  group,
  mixes,
  basketNumbers,
}: {
  group: FixedMixGroup;
  mixes: FixedMixCreate[];
  basketNumbers: number[];
}): ProductionMixResultRead | null => {
  const recipe = mixes.find((recipe) =>
    group.gradeNames.includes(recipe.steel_grade_name)
  );
  if (!recipe) return null;
  return {
    name: group.gradeNames.join(", "),
    description: null,
    production: group.gradeNames.map((steel_grade_name) => ({
      steel_grade_name,
      num_heats: 0,
    })),
    num_heats: 0,
    mix: {
      ...emptyFurnaceAndThresholds(),
      ...recipe.mix,
      id: group.mixId,
      baskets: [
        ...recipe.mix.baskets,
        ...basketNumbers
          .filter(
            (basketNumber) =>
              !recipe.mix.baskets.some(
                (basket) => basket.basket_number === basketNumber
              )
          )
          .map((basketNumber) => ({ basket_number: basketNumber, layers: [] })),
      ],
      basket_totals: [],
      summary: null,
    },
  };
};

export const fixedMixRows = ({
  groups,
  mixes,
  steelGrades,
  selectedGrades,
  basketNumbers,
  editedRows,
}: {
  groups: FixedMixGroup[];
  mixes: FixedMixCreate[];
  steelGrades: SteelGrade[];
  selectedGrades: string[];
  basketNumbers: number[];
  editedRows: Row[];
}): Row[] =>
  makeRows(
    null,
    minimumPeriod,
    groups.flatMap((group) => {
      if (
        selectedGrades.length > 0 &&
        !group.gradeNames.some((name) => selectedGrades.includes(name))
      )
        return [];
      const mixResult = fixedMixResult({ group, mixes, basketNumbers });
      return mixResult === null ? [] : [mixResult];
    }),
    steelGrades,
    "mass",
    basketNumbers
  ).map((row) => {
    const previous = editedRows.find(
      (previous) =>
        previous.mixId === row.mixId &&
        (row.type === "empty_profile"
          ? previous.type === "profile"
          : row.type === "empty_basket" &&
            previous.type === "basket" &&
            previous.basketNumber === row.basketNumber)
    );
    if (
      row.type === "empty_profile" &&
      previous?.type === "profile" &&
      previous.layers.length === 0
    )
      return { ...row, type: "profile" as const, layers: [] };
    if (
      row.type === "empty_basket" &&
      previous?.type === "basket" &&
      previous.layers.length === 0
    )
      return { ...row, type: "basket" as const, layers: [] };
    return row;
  });

export const applyFixedMixRows = ({
  groups,
  mixes,
  rows,
}: {
  groups: FixedMixGroup[];
  mixes: FixedMixCreate[];
  rows: Row[];
}): FixedMixCreate[] => {
  const updates = tableRowsToBackendMixes(rows);
  return mixes.map((recipe) => {
    const group = groups.find((group) =>
      group.gradeNames.includes(recipe.steel_grade_name)
    );
    const update = updates.find(
      (update) => update.original_mix_id === group?.mixId
    );
    return update?.mix
      ? { ...recipe, mix: { ...recipe.mix, baskets: update.mix.baskets } }
      : recipe;
  });
};

export const applyFixedMixLayers = ({
  gradeNames,
  mixes,
  baskets,
}: {
  gradeNames: string[];
  mixes: FixedMixCreate[];
  baskets: FixedMixCreate["mix"]["baskets"];
}): FixedMixCreate[] =>
  mixes.map((recipe) =>
    gradeNames.includes(recipe.steel_grade_name)
      ? { ...recipe, mix: { ...recipe.mix, baskets } }
      : recipe
  );
